import { useState } from "react";
import { ErrorBox, Loading, useNow } from "../components/ui.tsx";
import { useApi } from "../lib/api.ts";
import { duration } from "../lib/format.ts";
import type { Usage as UsageData } from "../lib/types.ts";

/** Fills in days with no events so the x-axis is continuous. */
function days(since: string, n: number): string[] {
  const out: string[] = [];
  const start = new Date(since);
  for (let i = 1; i <= n; i++) {
    const d = new Date(start.getTime() + i * 86_400_000);
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

/** One measure per chart (no dual axes); hover shows the exact value. */
function BarChart({ title, labels, values, format }: { title: string; labels: string[]; values: number[]; format: (v: number) => string }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...values);
  const total = values.reduce((a, b) => a + b, 0);
  return (
    <section className="card">
      <header>
        <h3>{title}</h3>
        <span className="muted small">{hover !== null ? `${labels[hover]}: ${format(values[hover]!)}` : `Total ${format(total)}`}</span>
      </header>
      <div className="bars" role="img" aria-label={`${title}, ${labels.length} days`} onMouseLeave={() => setHover(null)}>
        {values.map((v, i) => (
          <div key={labels[i]} className="bar" onMouseEnter={() => setHover(i)} title={`${labels[i]}: ${format(v)}`}>
            <span style={{ height: `${(v / max) * 100}%`, opacity: hover === null || hover === i ? 1 : 0.55 }} />
          </div>
        ))}
      </div>
      <div className="barlabels">
        {labels.map((l, i) => (
          <span key={l}>{i % Math.ceil(labels.length / 7) === 0 ? l.slice(5) : ""}</span>
        ))}
      </div>
    </section>
  );
}

export function Usage() {
  const [range, setRange] = useState(14);
  const usage = useApi<UsageData>(`/api/usage?days=${range}`);
  const now = useNow(60_000);
  if (!usage.data) return usage.error ? <ErrorBox error={usage.error} /> : <Loading />;
  const u = usage.data;
  const labels = days(u.since, u.days);
  const byDay = new Map(u.perDay.map((d) => [d.day, d]));
  const series = (k: "turns" | "durationMs" | "prompts" | "activeSessions") => labels.map((l) => byDay.get(l)?.[k] ?? 0);
  const totalMs = u.perDay.reduce((a, d) => a + d.durationMs, 0);
  const totalTurns = u.perDay.reduce((a, d) => a + d.turns, 0);
  const rateLimits = u.perDay.reduce((a, d) => a + d.rateLimited, 0);
  const created = u.sessionsCreated.reduce((a, d) => a + d.count, 0);
  const rl = u.rateLimit?.payload;
  const util = rl?.utilization !== undefined ? Math.round(rl.utilization * (rl.utilization <= 1 ? 100 : 1)) : null;
  const resetsAt = rl?.resetsAt ? rl.resetsAt * (rl.resetsAt < 1e12 ? 1000 : 1) : null;

  return (
    <>
      <div className="pagehead">
        <div>
          <h1>Usage</h1>
          <div className="sub">Where your Claude time went. Durations are wall-clock time per finished turn.</div>
        </div>
        <div className="seg">
          {[7, 14, 30].map((d) => (
            <button key={d} className={range === d ? "on" : ""} onClick={() => setRange(d)}>
              {d} days
            </button>
          ))}
        </div>
      </div>

      <div className="tiles">
        <div className="tile">
          <span className="n">{duration(totalMs)}</span>
          <span className="l">Claude working time</span>
        </div>
        <div className="tile">
          <span className="n">{totalTurns}</span>
          <span className="l">Turns finished</span>
        </div>
        <div className="tile">
          <span className="n">{created}</span>
          <span className="l">Sessions created</span>
        </div>
        <div className={`tile ${rateLimits ? "warn" : ""}`}>
          <span className="n">{rateLimits}</span>
          <span className="l">Rate-limit pauses</span>
        </div>
      </div>

      {util !== null && (
        <section className="card">
          <header>
            <h3>Max usage window</h3>
            <span className="muted small">
              {rl?.rateLimitType ?? "current window"} · {rl?.status}
              {resetsAt && resetsAt > now ? ` · resets in ${duration(resetsAt - now)}` : ""}
            </span>
          </header>
          <div className={`meter ${util >= 90 ? "bad" : util >= 70 ? "warn" : ""}`} role="meter" aria-valuenow={util} aria-valuemin={0} aria-valuemax={100} aria-label="Usage window">
            <span style={{ width: `${Math.min(100, util)}%` }} />
          </div>
          <div className="small">{util}% used (last reported by a runner)</div>
        </section>
      )}

      <div className="grid2">
        <BarChart title="Claude working time per day" labels={labels} values={series("durationMs")} format={(v) => duration(v)} />
        <BarChart title="Turns finished per day" labels={labels} values={series("turns")} format={(v) => String(v)} />
      </div>

      <section className="card flush">
        <header>
          <h3>By project</h3>
        </header>
        {u.perProject.length ? (
          <div className="tablewrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Working time</th>
                  <th>Share</th>
                  <th>Turns</th>
                  <th>Prompts</th>
                  <th>Sessions</th>
                </tr>
              </thead>
              <tbody>
                {u.perProject.map((p) => (
                  <tr key={p.projectId}>
                    <td>
                      <b>{p.name}</b>
                    </td>
                    <td className="mono small">{duration(p.durationMs)}</td>
                    <td style={{ minWidth: 120 }}>
                      <div className="meter">
                        <span style={{ width: `${totalMs ? (p.durationMs / totalMs) * 100 : 0}%` }} />
                      </div>
                    </td>
                    <td>{p.turns}</td>
                    <td>{p.prompts}</td>
                    <td>{p.activeSessions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty">No activity in this range.</div>
        )}
      </section>

      <details className="card">
        <summary>
          <h3 style={{ display: "inline" }}>Per-day table</h3>
        </summary>
        <div className="tablewrap" style={{ marginTop: 10 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Day</th>
                <th>Working time</th>
                <th>Turns</th>
                <th>Prompts</th>
                <th>Active sessions</th>
                <th>Errors</th>
                <th>Rate limits</th>
              </tr>
            </thead>
            <tbody>
              {[...u.perDay].reverse().map((d) => (
                <tr key={d.day}>
                  <td className="mono small">{d.day}</td>
                  <td className="mono small">{duration(d.durationMs)}</td>
                  <td>{d.turns}</td>
                  <td>{d.prompts}</td>
                  <td>{d.activeSessions}</td>
                  <td>{d.errors}</td>
                  <td>{d.rateLimited}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </>
  );
}
